import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./Header.module.scss";
import Person from "@/components/Person/Person";
import { useUserProfile } from "@/api/Users";
import { LoadingOverlay } from "@medoma/components/Loading/Loading";
import DropdownMenu from "@/components/DropdownMenu/DropdownMenu";
import LogoutIcon from "@medoma/components/icons/SVG/logout.svg?react";
import { Link, NavLink } from "react-router";
import {
  SmsEmployeeNotificationDropdownToggleItem,
  SmsPatientNotificationDropdownToggleItem,
} from "@/components/SmsNotificationDropdownToggleItem/SmsNotificationDropdownToggleItem";
import { useCurrentEmployee } from "@/api/Employee";
import { DropdownMenuGroup } from "@radix-ui/react-dropdown-menu";
import { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useRoomNamesWithUnreadNotifications } from "@medoma/chat/useUnreadNotificationCounts";
import UnreadChatMessagesIndicator from "@/components/IconWithIndicator/UnreadChatMessagesIndicator/UnreadChatMessagesIndicator";
import ChatContext from "@medoma/chat/ChatContext";
import { UnstyledLink } from "@medoma/components/Link/Link";
import { NotificationCircle } from "@medoma/components/NotificationCircle/NotificationCircle";
import ChatConversationIcon from "@medoma/components/icons/ChatConversation";
import { patientsWithUnhandledMeasurementsQueryOptions } from "@/api/Patients";
import { useQuery } from "@tanstack/react-query";
import UnhandledMeasurementsIndicator from "@/components/IconWithIndicator/UnhandledMeasurementsIndicator/UnhandledMeasurementsIndicator";
import { type IEmployee } from "@medoma/models/shifts";
import {
  isEmployeeRoomName,
  extractEmployeeIdFromRoomName,
} from "@medoma/chat/roomAlias";
import { CHAT_ENVIRONMENT, ENVIRONMENT } from "@/Utils/EnvUtils";
import { useAuth } from "react-oidc-context";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import LogoIconOnly from "@medoma/components/icons/LogoIconOnly";
import { IntercomButton } from "@medoma/support/IntercomButton";
import { shutdownIntercom } from "@medoma/support/shutdownIntercom";
import { clearUserSessionStorage } from "@medoma/shared-employee/userSessionStorage";
import { activeSidePanelsSchema, useSidePanel } from "./SidePanel/useSidePanel";
import UserGuideIcon from "@medoma/components/icons/UserGuideIcon";
import { SiteSwitcher } from "@/components/SiteSwitcher/SiteSwitcher";
import { GoSelfSignIn } from "./GoSelfSignIn";
import { organizationSupportsInvoicing } from "@medoma/models/organizations";
import { logisticsConfigQueryOptions } from "@/api/Config";
import { IconButton } from "@medoma/components/Button/Button";
import { useTheme, themeSchema } from "@/useTheme";

const Navigation = () => {
  const { organization } = useUserProfile();
  const { data: patientsWithUnhandledMeasurements } = useQuery(
    patientsWithUnhandledMeasurementsQueryOptions,
  );

  const { data: equipmentManagementEnabled } = useQuery({
    ...logisticsConfigQueryOptions(),
    select: (data) => data.equipmentManagement.enabled,
  });

  const roomNamesWithUnreadNotifications =
    useRoomNamesWithUnreadNotifications();
  const roomNamesWithUnreadNotificationsExceptEmployees =
    roomNamesWithUnreadNotifications.filter(
      (roomName) => !isEmployeeRoomName(roomName, organization),
    );
  const patientRoomsHaveUnreadChatNotifications =
    roomNamesWithUnreadNotificationsExceptEmployees !== undefined &&
    roomNamesWithUnreadNotificationsExceptEmployees.length > 0;

  const patientsHaveUnhandledMeasurements =
    patientsWithUnhandledMeasurements !== undefined &&
    patientsWithUnhandledMeasurements.some(
      ({ unhandledMeasurementsCount }) => unhandledMeasurementsCount > 0,
    );

  const { data: shouldShowShiftManipulation } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.PERMANENT_ShowShiftManipulationInUI,
  );

  const { data: capacityOverviewEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.FE_CapacityOverview,
  );

  return (
    <nav>
      <ul className={styles.navItems}>
        <li>
          <NavLink to="patients" className={styles.navItem}>
            <Trans>Patienter</Trans>
            <UnreadChatMessagesIndicator
              hasUnreadMessages={
                patientRoomsHaveUnreadChatNotifications ?? false
              }
            />
            <UnhandledMeasurementsIndicator
              hasUnhandledMeasurements={patientsHaveUnhandledMeasurements}
            />
          </NavLink>
        </li>
        <li>
          <NavLink to="activities" className={styles.navItem}>
            <Trans>Aktiviteter</Trans>
          </NavLink>
        </li>
        <li>
          <NavLink to="planning" className={styles.navItem}>
            <Trans>Planering</Trans>
          </NavLink>
        </li>
        {capacityOverviewEnabled ? (
          <li>
            <NavLink to="capacity" className={styles.navItem}>
              <Trans>Kapacitet</Trans>
            </NavLink>
          </li>
        ) : null}
        {shouldShowShiftManipulation ? (
          <li>
            <NavLink to="shifts" className={styles.navItem}>
              <Trans context="plural">Medarbetare</Trans>
            </NavLink>
          </li>
        ) : null}
        {equipmentManagementEnabled ? (
          <li>
            <NavLink to="equipment" className={styles.navItem}>
              <Trans>Utrustning</Trans>
            </NavLink>
          </li>
        ) : null}
        {organizationSupportsInvoicing(organization) ? (
          <li>
            <NavLink to="reporting" className={styles.navItem}>
              <Trans>Rapportering</Trans>
            </NavLink>
          </li>
        ) : (
          <></>
        )}
      </ul>
    </nav>
  );
};

const ChatLink = () => {
  const { organization } = useUserProfile();
  const roomNamesWithUnreadNotifications =
    useRoomNamesWithUnreadNotifications();
  const employeeIdsWithUnreadNotifications = new Set(
    roomNamesWithUnreadNotifications
      .filter((name) => isEmployeeRoomName(name, organization))
      .map((name) => extractEmployeeIdFromRoomName(name, CHAT_ENVIRONMENT))
      .filter((id): id is string => id !== null),
  );

  const { matrixClient } = useContext(ChatContext);
  const hasCompletedInitialSync = !!matrixClient;

  const numberOfEmployeesWithUnreadNotifications =
    employeeIdsWithUnreadNotifications.size || undefined;

  return (
    <UnstyledLink to="chat">
      <div className={styles.iconLink}>
        <LoadingOverlay show={!hasCompletedInitialSync}>
          <NotificationCircle
            isVisible={!!numberOfEmployeesWithUnreadNotifications}
            placement="top-left"
            number={numberOfEmployeesWithUnreadNotifications}
          >
            <ChatConversationIcon />
          </NotificationCircle>
        </LoadingOverlay>
      </div>
    </UnstyledLink>
  );
};

const CustomTrigger = ({
  givenName,
  employee,
}: {
  givenName: string;
  employee: IEmployee | undefined;
}) => <Person name={givenName} photoUrl={employee?.imageURL ?? null} />;

const ThemeSelector = () => {
  const [theme, setTheme] = useTheme();
  return (
    <>
      <DropdownMenu.RadioGroup value={theme} onValueChange={setTheme}>
        <DropdownMenu.Label>
          <Trans>Färgtema</Trans>
        </DropdownMenu.Label>
        <DropdownMenu.RadioItem
          value={themeSchema.enum.warm}
          label={<Trans>Varmt</Trans>}
        />
        <DropdownMenu.RadioItem
          value={themeSchema.enum.white}
          label={<Trans>Vitt</Trans>}
        />
      </DropdownMenu.RadioGroup>
    </>
  );
};

const Header = () => {
  const { t } = useLingui();
  const { givenName, organization, employeeNumber } = useUserProfile();
  const { data: employee } = useCurrentEmployee();
  const { data: isAIPlatformAssistantEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.AIPlatformAssistantInCenter,
  );

  useEffect(() => {
    Sentry.setUser({
      username: `${ENVIRONMENT === "local" ? "dev" : ENVIRONMENT}-${organization}-${employeeNumber}`,
    });
  }, [organization, employeeNumber]);

  const auth = useAuth();

  // Employees might share computers and OS accounts, so signing out has to
  // leave the tab with nothing of this employee's in it. Both teardowns run
  // here rather than in an unmount cleanup: the messenger is booted from
  // IntercomProvider's render body, so it is up even when the effect returned
  // early on a pending token and left no cleanup behind, and the tab-scoped
  // storage has no component to hang a cleanup on at all.
  const signOut = () => {
    shutdownIntercom();
    clearUserSessionStorage();
    void auth.signoutRedirect({
      post_logout_redirect_uri: window.location.origin,
    });
  };

  const { toggleSidePanel } = useSidePanel();

  return (
    <header className={styles.header}>
      <Link aria-label={t`Startsida`} to="/" className={styles.logo}>
        <LogoIconOnly />
      </Link>
      <SiteSwitcher />
      <Navigation />
      <div className={styles.rightContainer}>
        <div className={styles.communicationIcons}>
          <ChatLink />
          <IntercomButton />
          {isAIPlatformAssistantEnabled ? (
            <div className={styles.iconButtonWrapper}>
              <IconButton
                onClick={() =>
                  toggleSidePanel(activeSidePanelsSchema.enum.aiSupportPanel)
                }
                aria-label={t`Handboken`}
              >
                <UserGuideIcon />
              </IconButton>
            </div>
          ) : null}
        </div>
        <DropdownMenu
          trigger={{
            custom: <CustomTrigger givenName={givenName} employee={employee} />,
            ariaLabel: t`Meny`,
          }}
        >
          <DropdownMenuGroup>
            <DropdownMenu.Label>
              <Trans>SMS-notiser för chatt</Trans>
            </DropdownMenu.Label>
            <SmsPatientNotificationDropdownToggleItem />
            <SmsEmployeeNotificationDropdownToggleItem />
          </DropdownMenuGroup>
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            content={<GoSelfSignIn />}
            shouldCloseMenuOnSelect={false}
          />
          <DropdownMenu.Separator />
          <ThemeSelector />
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            action={signOut}
            content={
              <>
                <LogoutIcon /> <Trans>Logga ut</Trans>
              </>
            }
          />
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
